import { useEffect, useRef, useState } from 'react';

export type HeadroomState = 'top' | 'pinned' | 'unpinned' | 'bottom';

type UseHeadroomOptions = {
  /** Vertical offset in px before headroom kicks in. Default: 0 */
  offset?: number;
  /** Scroll tolerance in px before state changes. Default: { up: 5, down: 0 } */
  tolerance?: number | { up: number; down: number };
  /** Element to listen to scroll events on. Default: window */
  scrollParent?: HTMLElement | null;
};

const getScrollY = (el?: HTMLElement | null): number =>
  el ? el.scrollTop : window.scrollY;

const getScrollHeight = (el?: HTMLElement | null): number =>
  el ? el.scrollHeight : document.documentElement.scrollHeight;

const getClientHeight = (el?: HTMLElement | null): number =>
  el ? el.clientHeight : window.innerHeight;

export function useHeadroom({
  offset = 0,
  tolerance = { up: 5, down: 0 },
  scrollParent,
}: UseHeadroomOptions = {}): HeadroomState {
  const [state, setState] = useState<HeadroomState>('top');
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const toleranceUp =
      typeof tolerance === 'number' ? tolerance : tolerance.up;
    const toleranceDown =
      typeof tolerance === 'number' ? tolerance : tolerance.down;

    const update = () => {
      const scrollY = getScrollY(scrollParent);
      const scrollHeight = getScrollHeight(scrollParent);
      const clientHeight = getClientHeight(scrollParent);

      const isOutOfBounds =
        scrollY < 0 || scrollY + clientHeight > scrollHeight;

      if (!isOutOfBounds) {
        const direction = scrollY > lastScrollY.current ? 'down' : 'up';
        const delta = Math.abs(scrollY - lastScrollY.current);
        const atTop = scrollY <= offset;
        const atBottom = scrollY + clientHeight >= scrollHeight;

        if (atTop) {
          setState('top');
        } else if (atBottom) {
          setState('bottom');
        } else if (direction === 'down' && delta >= toleranceDown) {
          setState('unpinned');
        } else if (direction === 'up' && delta >= toleranceUp) {
          setState('pinned');
        }

        lastScrollY.current = scrollY;
      }

      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(update);
        ticking.current = true;
      }
    };

    const target = scrollParent ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
  }, [offset, tolerance, scrollParent]);

  return state;
}
